import {isUndefined} from 'lodash';
import classnames from 'classnames';
import {Map} from 'immutable';
import React from 'react';
import {callAction} from 'client/framework';

import {BasicTable, LoadingSpinnerLong} from '@albert-io/atomic';
import MarkdownRendererV2 from 'generic/MarkdownRendererV2/MarkdownRendererV2.react';
import {getDifficultySpan, getFormattedDurationTimeFromStamp} from 'lib/questionRenderUtils';
import HealthIndicator from 'generic/HealthIndicator/HealthIndicator.react';
import Tooltip from 'generic/FixedTooltip/FixedTooltip.react';
import * as ClassroomUtil from 'client/Classrooms/ClassroomUtil';
import {stats} from 'lib/StatsUtil';
import {ReportsLinkBanner} from 'client/Reports/ReportsLinkBanner.react';

import AssignmentDrilldownStore from '../AssignmentDrilldownStore';

import QuestionsActions from './QuestionsActions';
import QuestionsStore from './QuestionsStore';

/*

  TODO:
    - Make this guy have its own store that makes its own calls for stuff
*/
export default class Questions extends React.Component {
  componentDidMount() {
    callAction(
      QuestionsActions.INITIALIZE_QUESTION_LIST,
      new Map({
        // eslint-disable-next-line react/prop-types
        assignmentId: this.props.params.assignmentId,
        // eslint-disable-next-line react/prop-types
        classroomId: this.props.params.classId
      })
    );
  }

  getPercentageString(num, denom) {
    if (isUndefined(denom) || denom === 0) {
      return '';
    }
    // Hyphen added here so we can fully turn on/off the percentage display
    return ` - ${stats.percentage(num, denom)}%`;
  }

  makeHealthTooltip(questionTableData) {
    const studentsThatHaveAnsweredCorrectly = questionTableData.get(
      'studentsThatHaveAnsweredCorrectly'
    );
    const studentsThatHaveAnsweredIncorrectly = questionTableData.get(
      'studentsThatHaveAnsweredIncorrectly'
    );
    const studentsThatHaveNotAnswered = questionTableData.get('studentsThatHaveNotAnswered');
    const studentsThatHaveAnswered = questionTableData.get('studentsThatHaveAnswered');
    const totalStudents = questionTableData.get('totalStudents');

    const correctString = `(${studentsThatHaveAnsweredCorrectly}/${studentsThatHaveAnswered})${this.getPercentageString(
      studentsThatHaveAnsweredCorrectly,
      studentsThatHaveAnswered
    )}`;
    const incorrectString = `(${studentsThatHaveAnsweredIncorrectly}/${studentsThatHaveAnswered})${this.getPercentageString(
      studentsThatHaveAnsweredIncorrectly,
      studentsThatHaveAnswered
    )}`;
    const unansweredString = `(${studentsThatHaveNotAnswered}/${totalStudents})${this.getPercentageString(
      studentsThatHaveNotAnswered,
      totalStudents
    )}`;

    return (
      <div className='insight-table__question-answer-badge-tooltip'>
        <div className='insight-table__question-answer-badge-tooltip-datum'>
          <dt className='insight-table__question-answer-badge-tooltip-term'>Correct</dt>
          <dd className='insight-table__question-answer-badge-tooltip-definition'>
            {correctString}
          </dd>
        </div>
        <div className='insight-table__question-answer-badge-tooltip-datum'>
          <dt className='insight-table__question-answer-badge-tooltip-term'>Incorrect </dt>
          <dd className='insight-table__question-answer-badge-tooltip-definition'>
            {incorrectString}
          </dd>
        </div>
        <div className='insight-table__question-answer-badge-tooltip-datum'>
          <dt className='insight-table__question-answer-badge-tooltip-term'>Not Yet Answered</dt>
          <dd className='insight-table__question-answer-badge-tooltip-definition'>
            {unansweredString}
          </dd>
        </div>
      </div>
    );
  }

  makeRowForQuestion(questionData, questionTableData) {
    const questionId = questionData.get('id');
    const isUnanswered = questionTableData.get('isUnanswered');
    return (
      <BasicTable.Row
        key={questionData.get('id')}
        className={classnames('insight-table__row--no-border', {
          'insight-table__row-disabled': isUnanswered
        })}
      >
        <BasicTable.Cell
          className='insight-table__question-info'
          contentType='graphic'
          onClick={ClassroomUtil.makeNavigateToQuestion(
            questionTableData.get('questionSetSlug'),
            questionTableData.get('questionSlug')
          )}
        >
          <div className='insight-table__question-info-difficulty'>
            {getDifficultySpan(
              AssignmentDrilldownStore.getQuestionById(questionId).getDifficulty(),
              isUnanswered
            )}
          </div>
          <div className='insight-table__question-info-name'>
            {/*
             * questionTableData.get('questionName') was being populated with the question set name in some subjects
             * Because this very old view is getting a rework, grabbing the question title from questionData works for now
             */}
            <MarkdownRendererV2 text={questionData.getTitle()} />
          </div>
        </BasicTable.Cell>
        <BasicTable.Cell align='center'>
          {isUnanswered
            ? 'n/a'
            : getFormattedDurationTimeFromStamp(questionTableData.get('avgDuration'))}
        </BasicTable.Cell>
        <BasicTable.Cell contentType='graphic' align='center'>
          <div className='insight-table__health-indicator'>
            <HealthIndicator
              passingPercentage={0}
              strugglingPercentage={questionTableData.get('incorrectPercentage')}
              excellingPercentage={questionTableData.get('correctPercentage')}
              emptypercentage={questionTableData.get('unansweredPercentage')}
            />
            <Tooltip>{this.makeHealthTooltip(questionTableData)}</Tooltip>
          </div>
        </BasicTable.Cell>
      </BasicTable.Row>
    );
  }

  render() {
    if (
      !AssignmentDrilldownStore.hasActiveAssignment() ||
      !QuestionsStore.hasQuestionStats() ||
      !AssignmentDrilldownStore.hasAssignmentStats()
    ) {
      return (
        <div className='assignment-drilldown__questions u-display_flex u-align-items_center u-justify-content_center u-pad-tb_5'>
          <LoadingSpinnerLong />
        </div>
      );
    }

    // TODO: fold this into QuestionsStore
    const activeAssignmentQuestions = AssignmentDrilldownStore.getActiveAssignmentQuestions();
    const tableHeadings = [
      {
        label: 'Question',
        align: 'left'
      },
      {
        label: 'Avg. Duration',
        align: 'center'
      },
      {
        label: 'Performance Breakdown',
        align: 'center'
      }
    ];
    const questionTableData = QuestionsStore.getQuestionTableData();

    const classroomId = AssignmentDrilldownStore.readData('classroomId');
    const assignmentId = AssignmentDrilldownStore.getActiveAssignment().getId();

    return (
      <div className='assignment-drilldown__questions'>
        <ReportsLinkBanner
          className='u-mar_2'
          link={`/reports/classrooms/${classroomId}/assignments/${assignmentId}/questions`}
        />
        <BasicTable>
          <BasicTable.Head className='insight-table__table-header--max'>
            <BasicTable.Row>
              {tableHeadings.map((heading, key) => {
                return (
                  <BasicTable.Header align={heading.align} key={key}>
                    {heading.label}
                  </BasicTable.Header>
                );
              })}
            </BasicTable.Row>
          </BasicTable.Head>
          <BasicTable.Body>
            {activeAssignmentQuestions.map((questionData) => {
              return this.makeRowForQuestion(
                questionData,
                questionTableData.get(questionData.get('id'))
              );
            })}
          </BasicTable.Body>
        </BasicTable>
      </div>
    );
  }
}
